import cn from 'classnames';
import * as Accordion from '@radix-ui/react-accordion';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { Container } from 'react-scale/components/pages/zeitgeist/layout/container';
import { useDeviceDetect } from 'react-scale/hooks/use-device-detect';
import { gsap } from 'react-scale/lib/gsap';
import { useMedia } from 'react-use';
const Spline = dynamic(import('@splinetool/react-spline'), {
  ssr: false
});

import s from './graph.module.scss';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';

const IMAGES = [
  '/static/zeitgeist/2023/graph/tooltip-1.png',
  '/static/zeitgeist/2023/graph/tooltip-2.png',
  '/static/zeitgeist/2023/graph/tooltip-3.png',
  '/static/zeitgeist/2023/graph/tooltip-4.png'
];

export const Graph = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [opacities, setOpacities] = useState<number[]>([]);
  const bubbleRef = useRef<HTMLDivElement>();
  const triggersRef = useRef<HTMLButtonElement[]>([]);
  const [loaded, setLoaded] = useState(false);
  const isDesktop = useMedia('(min-width: 1024px)');
  const [sliderRef, slider] = useKeenSlider({
    loop: true,
    slides: IMAGES.length,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
    detailsChanged(s) {
      const new_opacities = s.track.details.slides.map(slide => slide.portion);
      setOpacities(new_opacities);
    }
  });

  useEffect(() => {
    if (!bubbleRef.current || triggersRef.current.length < STATS.length || !isDesktop) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!bubbleRef.current) return;

      const mouseX = e.clientX;
      const mouseY = e.clientY;
      bubbleRef.current.style.left = mouseX + 'px';
      bubbleRef.current.style.top = mouseY + 'px';
    };

    const handleMouseEnter = (e: MouseEvent) => {
      if ((e.target as HTMLButtonElement).dataset.state === 'open') return;

      const mouseX = e.clientX;
      const mouseY = e.clientY;
      bubbleRef.current.style.left = mouseX + 'px';
      bubbleRef.current.style.top = mouseY + 'px';

      document.addEventListener('mousemove', handleMouseMove);
      gsap.to(bubbleRef.current, {
        scale: 1,
        duration: 0.3
      });
    };
    const handleMouseLeave = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      gsap.to(bubbleRef.current, {
        scale: 0,
        duration: 0.3
      });
    };
    const handlePointerDown = (e: MouseEvent) => {
      if ((e.target as HTMLButtonElement).dataset.state === 'open') return;

      gsap.to(bubbleRef.current, { scale: 0.9, duration: 0.08, ease: 'linear' });
    };
    const handlePointerUp = () => gsap.to(bubbleRef.current, { scale: 0 });

    triggersRef.current.forEach(trigger => {
      if (!trigger) return;
      trigger.addEventListener('pointerdown', handlePointerDown);
      trigger.addEventListener('pointerup', handlePointerUp);
      trigger.addEventListener('mouseenter', handleMouseEnter);
      trigger.addEventListener('mouseleave', handleMouseLeave);
    });

    return () => {
      triggersRef.current.forEach(trigger => {
        if (!trigger) return;
        trigger.removeEventListener('pointerdown', handlePointerDown);
        trigger.removeEventListener('pointerup', handlePointerUp);
        trigger.removeEventListener('mouseenter', handleMouseEnter);
        trigger.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [isDesktop]);

  return (
    <div className={s.section}>
      <Container size="sm" className="lg:!pl-0">
        <div className={s.grid}>
          <div className={s.graph}>
            {isDesktop && (
              <Spline
                scene="https://prod.spline.design/gZB8o2h63Ghsi-Pl/scene.splinecode"
                className="!w-full !h-auto aspect-[1043/755]"
              />
            )}

            <div className="lg:hidden">
              <Image
                className="absolute"
                src="/static/zeitgeist/2023/graph/graphic.png"
                width={746}
                height={530}
                quality={100}
                alt="Graph"
              />

              <div ref={sliderRef} className="relative aspect-square">
                {IMAGES.map((img, index) => (
                  <div
                    key={index}
                    className="w-full h-full absolute top-0"
                    style={{ opacity: opacities[index] }}
                  >
                    <Image
                      src={img}
                      width={746}
                      height={530}
                      quality={100}
                      alt={`Tooltip number ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              {loaded && slider && (
                <div className="flex justify-center mx-auto mt-6 space-x-2 max-w-max lg:hidden">
                  {[...Array(IMAGES.length).keys()].map(idx => (
                    <span
                      key={idx}
                      onClick={() => slider.current.moveToIdx(idx)}
                      className={cn(
                        'rounded-full w-2 h-2 transition-all duration-200 ease-out',
                        currentSlide === idx ? 'bg-[#D9D9D9] scale-125' : 'bg-[#515151]'
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div ref={bubbleRef} className={s['bubble']}>
            OPEN
          </div>

          <Accordion.Root type="single" defaultValue="item-1">
            {STATS.map(({ accordionValue, title, strong, body }, index) => (
              <Accordion.Item key={index} className={s['accordion__item']} value={accordionValue}>
                <Accordion.Trigger
                  ref={ref => triggersRef.current.push(ref)}
                  className={s['accordion__trigger']}
                >
                  {title}
                </Accordion.Trigger>
                <Accordion.Content className={s['accordion__content']}>
                  <div className={s['accordion-content__text']}>
                    <h6 className={s['accordion__title']}>{strong}</h6>
                    <p className={s['accordion__paragraph']}>{body}</p>
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </div>
      </Container>
    </div>
  );
};

const STATS = [
  {
    accordionValue: 'item-1',
    title: 'Generative AI Takes a Giant Leap',
    strong: 'Improved Generative Models Force Companies to Accelerate their AI Strategies',
    body: 'The significantly improved capabilities of large generative models in 2022 had an enormous impact on the business community in 2022, causing the 65% of respondents to either increase their AI budgets or create an AI strategy for the first time. In this report we deeply explore the impact of Generative AI, and AI more broadly, and how companies can best plan to adopt AI in 2023.'
  },
  {
    accordionValue: 'item-2',
    title: '72% plan to increase their AI investments this year',
    strong: '72% of survey respondents planning to increase their AI investments this year',
    body: 'As Generative AI is now more capable and widely available, companies are quickly incorporating it into their operations. 72% of companies will significantly increase their investment in AI each year for the next three years.'
  },
  {
    accordionValue: 'item-3',
    title: 'Positive outcomes for companies adopting AI',
    strong:
      '89% of companies adopting AI benefit from the ability to develop new products or services',
    body: '78% from enhanced customer experiences, and 76% from better collaboration across business functions. These companies also see improved organizational process efficiency and profitability.'
  }
];
